import { DestroyRef, inject, signal, computed, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Apollo, QueryRef } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { map, filter, tap, switchMap, shareReplay, startWith, catchError, debounceTime } from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';

type WatchQueryFetchPolicy = 'cache-first' | 'network-only' | 'cache-and-network' | 'cache-only' | 'no-cache' | 'standby';

export interface ReactiveContainerConfig {
  fetchPolicy?: WatchQueryFetchPolicy;
  pollInterval?: number;
  stopPollingOnSuccess?: boolean;
  useGlobalLoading?: boolean;
}

export interface FromSignalsOptions<TInputs> {
  skipWhen?: (values: TInputs) => boolean;
  debounceMs?: number;
}

export class ReactiveContainer<TData, TVariables extends Record<string, any> = Record<string, any>> {
  private readonly apollo = inject(Apollo);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingService = inject(LoadingService);

  private readonly _data = signal<TData | null>(null);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<Error | null>(null);

  readonly data: Signal<TData | null> = this._data.asReadonly();
  readonly loading: Signal<boolean> = this._loading.asReadonly();
  readonly error: Signal<Error | null> = this._error.asReadonly();

  private readonly reload$ = new Subject<void>();
  private queryRef: QueryRef<TData, TVariables> | null = null;
  private hasLoadedOnce = false;
  private readonly config: Required<ReactiveContainerConfig>;

  constructor(
    private readonly query: DocumentNode,
    config: ReactiveContainerConfig = {}
  ) {
    this.config = {
      fetchPolicy: 'cache-and-network',
      pollInterval: 0,
      stopPollingOnSuccess: true,
      useGlobalLoading: true,
      ...config
    };
  }

  execute(variables: TVariables): void {
    this.fetchQuery(variables)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  refetch(variables: TVariables): void {
    this.execute(variables);
  }

  reload(): void {
    if (this.queryRef) {
      this.queryRef.refetch();
    } else {
      this.reload$.next();
    }
  }

  fromSignals<TInputs extends readonly Signal<any>[]>(
    signals: [...TInputs],
    buildVariables: (values: { [K in keyof TInputs]: TInputs[K] extends Signal<infer V> ? V : never }) => TVariables,
    options?: FromSignalsOptions<{ [K in keyof TInputs]: TInputs[K] extends Signal<infer V> ? V : never }>
  ): Signal<TData | null> {
    const observables = signals.map(s => toObservable(s));

    const debounceMs = options?.debounceMs ?? 10;
    let source$ = combineLatest(observables);
    if (debounceMs > 0) {
      source$ = source$.pipe(debounceTime(debounceMs));
    }

    const query$ = source$.pipe(
      filter(values => {
        if (options?.skipWhen) {
          return !options.skipWhen(values as any);
        }
        return true;
      }),
      switchMap(values =>
        this.reload$.pipe(
          startWith(undefined),
          map(() => values)
        )
      ),
      switchMap(values => {
        const variables = buildVariables(values as any);
        return this.fetchQuery(variables);
      }),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef)
    );

    query$.subscribe();

    return this.data;
  }

  select<TResult>(selector: (data: TData) => TResult): Signal<TResult | null> {
    return computed(() => {
      const data = this.data();
      return data ? selector(data) : null;
    });
  }

  selectWithDefault<TResult>(selector: (data: TData) => TResult, defaultValue: TResult): Signal<TResult> {
    return computed(() => {
      const data = this.data();
      return data ? selector(data) : defaultValue;
    });
  }

  private fetchQuery(variables: TVariables): Observable<TData | null> {
    if (!this.hasLoadedOnce) {
      this._loading.set(true);
      if (this.config.useGlobalLoading) {
        this.loadingService.setLoading(true);
      }
    }
    this._error.set(null);

    this.queryRef = this.apollo.watchQuery<TData, TVariables>({
      query: this.query,
      variables,
      fetchPolicy: this.config.fetchPolicy,
      errorPolicy: 'all',
      pollInterval: this.config.pollInterval
    });

    return this.queryRef.valueChanges.pipe(
      tap(result => {
        const isLoading = result.networkStatus === 1 ||
          result.networkStatus === 2 ||
          result.networkStatus === 4;

        if (!isLoading && result.data) {
          this._loading.set(false);
          if (this.config.useGlobalLoading) {
            this.loadingService.setLoading(false);
          }

          this.hasLoadedOnce = true;

          if (this.config.stopPollingOnSuccess && this.config.pollInterval > 0) {
            this.stopPolling();
          }
        }

        if (result.errors?.length) {
          this._error.set(new Error(result.errors[0].message));
        }

        if (result.data) {
          this._data.set(result.data);
        }
      }),
      filter(result => {
        return result.data !== undefined &&
          result.networkStatus !== 2 &&
          result.networkStatus !== 4;
      }),
      map(result => result.data ?? null),
      catchError(err => {
        this._loading.set(false);
        this._error.set(err);
        if (this.config.useGlobalLoading) {
          this.loadingService.setLoading(false);
        }
        console.error('ReactiveContainer query failed:', err);
        return of(null);
      })
    );
  }

  stopPolling(): void {
    this.queryRef?.stopPolling();
  }

  startPolling(interval?: number): void {
    const pollInterval = interval ?? this.config.pollInterval;
    if (pollInterval > 0) {
      this.queryRef?.startPolling(pollInterval);
    }
  }

  getQueryRef(): QueryRef<TData, TVariables> | null {
    return this.queryRef;
  }
}

export function createReactiveContainer<TData, TVariables extends Record<string, any> = Record<string, any>>(
  query: DocumentNode,
  config?: ReactiveContainerConfig
): ReactiveContainer<TData, TVariables> {
  return new ReactiveContainer<TData, TVariables>(query, config);
}
